import { Button } from '@components/Button';

import Block from '@components/Layout/Block';
import { VerticalLineSeparator } from '@components/MiscComponents/VerticalLineSeparator';
import PropertyCard from '@components/PropertiesSearch/PropertyCard';
import {
  Listing,
  ListingSortKeys,
  PropertyTypeEnum,
  SortDir,
  StandardStatusEnum,
} from '@engelvoelkersamericas/api';
import { getCorrectHeader, getCurrentLocale } from '@util/locale';
import { apiClient } from 'api-client';
import { useTranslations } from 'next-intl';
import { FC, ReactNode, useEffect, useState } from 'react';
import CardGridWithMobileCarousel from '..';
import { CarouselCtaWrapper, CarouselHeaderWrapper, CarouselHeadline } from '../style';

export interface ShopsNewestListingsProps {
  newestListingsHeader: string;
  showNewestListings?: boolean;
  ctaUrl?: string;
  shopIds?: string;
  neighborhoodCardArr?: Listing[] | null;
  isNeighborhoodPage?: boolean;
}

const ShopsNewestListings: FC<ShopsNewestListingsProps> = ({
  newestListingsHeader,
  ctaUrl,
  shopIds,
  showNewestListings,
  neighborhoodCardArr,
  isNeighborhoodPage,
}) => {
  const [cardArr, setCardArr] = useState<Listing[]>([]);
  const t = useTranslations();
  const locale = getCurrentLocale();

  const shopsNewestListingsHeaderProcessed = getCorrectHeader(
    { headerData: newestListingsHeader },
    locale,
  );

  const fetchAndSetCardArr = async () => {
    if (isNeighborhoodPage && neighborhoodCardArr) {
      setCardArr(neighborhoodCardArr);
    } else if (shopIds) {
      try {
        const data = await apiClient.getListings({
          page: 1,
          size: 6,
          sortKey: ListingSortKeys.OnMarketDate,
          sortDir: SortDir.Desc,
          listingFilters: {
            listingStatuses: [StandardStatusEnum.Active],
            shopIds: shopIds.split(','),
            propertyTypes: [PropertyTypeEnum.Residential],
            evOnly: true,
          },
        });

        setCardArr(data?.data?.listings);
      } catch (error) {
        console.error(error);
        setCardArr([]);
      }
    }
  };

  // if shopId is provided, fetch data from the API
  useEffect(() => {
    fetchAndSetCardArr();
  }, [shopIds, isNeighborhoodPage]);

  const cardElements: ReactNode[] = cardArr.map((card: any, ind) => {
    return <PropertyCard key={ind} listing={card} idx={ind} />;
  });

  return (
    cardArr.length > 0 &&
    showNewestListings != false && (
      <Block>
        <CarouselHeaderWrapper>
          <CarouselHeadline data-testid="newest-listings-header">
            {shopsNewestListingsHeaderProcessed
              ? shopsNewestListingsHeaderProcessed
              : t('Newest Listings')}
          </CarouselHeadline>
        </CarouselHeaderWrapper>

        <VerticalLineSeparator />

        <CardGridWithMobileCarousel
          dataTestId="newest-listings"
          cardType="property"
          cardArr={cardElements}
        />

        <CarouselCtaWrapper>
          <Button
            dataTestId="view-all-listings-btn"
            url={ctaUrl ?? '/properties'}
            label={t('View All Listings')}
            variant="secondary"
            arrow={false}
          />
        </CarouselCtaWrapper>
      </Block>
    )
  );
};

export default ShopsNewestListings;
